import i18n from "i18next";
import { LazyMultiSelectField } from "src/components/common/inputFields/LazyMultiSelectField.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import {
	booleanColumn,
	dateTimeColumn,
	integerColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import { DocumentForm } from "src/components/views/documents/DocumentForm.tsx";
import { DocumentsOfObjectDataGridApi } from "src/api/generated/documents/api/documentsOfObjectDataGridApi.ts";
import { DocumentsOfObjectDataGridContextMenu } from "src/components/views/documents/objectDocuments/DocumentsOfObjectDataGridContextMenu.tsx";
import { faExternalLink, faFileArrowDown, faLink } from "@fortawesome/pro-regular-svg-icons";
import { DocumentDropZone } from "src/components/views/documents/import/DocumentDropZone.tsx";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { DocumentPreviewView } from "src/components/views/documents/preview/DocumentPreviewView.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { DocumentFileApi } from "src/api/generated/documents/api/documentFileApi.ts";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { thumbnailColumn } from "src/components/views/documents/utils/thumbnails.tsx";
import { GridRowWithFileDropping } from "src/components/common/dataGrid/dnd/GridRowWithFileDropping.tsx";
import { DropFileToDocumentView } from "src/components/views/documents/import/DropFileToDocumentView.tsx";
import { GenericDialogContext } from "src/components/common/dialogs/GenericDialogProvider.tsx";
import { useServerSideDataModel } from "src/components/common/dataGrid/useServerSideDataModel.ts";
import { ControlledAsyncCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncCrudDataGrid";
import { DocumentConnectionView } from "src/api/generated/postgres/db/types/documents/tables/documentConnectionView.ts";
import { ConnectDocumentsToObjectView } from "src/components/views/documents/connections/ConnectDocumentsToObjectView.tsx";
import { useContextOrThrow } from "src/utils/useContextOrThrow.tsx";
import { AavoObjectRef } from "src/api/generated/common/sourceType/aavoObjectRef.ts";

export interface DocumentsOfObjectDataGridProps {
	objectRef: AavoObjectRef;
	editable: boolean;
	onSelectionChanged: (documents: DocumentConnectionView[]) => void;
	onDocumentFileChanged: () => void;
}

export const DocumentsOfObjectDataGrid = ({
	objectRef,
	editable,
	onSelectionChanged,
	onDocumentFileChanged,
}: DocumentsOfObjectDataGridProps) => {
	const { serverSideDataGridProps, currentParams, refreshData, selectedRows, onlySelectedRow } =
		useServerSideDataModel({
			gridId: "60AEF12D15AD6E54",
			fetchData: DocumentsOfObjectDataGridApi.getDocumentsOfObject,
			getRowId: (row) => row.documentId,
			initialParams: {
				objectRef,
				categoryFilter: Array<number>(),
				searchQuery: "",
			},
			afterRefresh: onDocumentFileChanged,
			onSelectionChanged: onSelectionChanged,
		});
	const selectedDocumentIds = selectedRows.map((row) => row.documentId);

	const { openDialog } = useContextOrThrow(GenericDialogContext);

	const onFilesDroppedToRow = (row: DocumentConnectionView, files: File[]) => {
		openDialog(({ closeDialog }) => ({
			title: i18n.t("new_file"),
			size: "sm",
			children: (
				<DropFileToDocumentView
					file={files[0]!}
					documentId={row.documentId}
					afterSubmit={async () => {
						await closeDialog();
						await refreshData();
					}}
				/>
			),
		}));
	};

	return (
		<ControlledAsyncCrudDataGrid<DocumentConnectionView>
			columns={[
				integerColumn({
					headerName: i18n.t("number_shortened"),
					field: "documentId",
					width: 60,
				}),
				textColumn({
					headerName: i18n.t("description_1"),
					field: "description1",
					width: 300,
				}),
				textColumn({
					headerName: i18n.t("type"),
					field: "primaryFileExtension",
					width: 80,
				}),
				integerColumn({
					headerName: i18n.t("revision"),
					field: "revisionNumber",
					width: 60,
				}),
				thumbnailColumn({
					headerName: i18n.t("image"),
					field: "thumbnail",
				}),
				textColumn({
					headerName: i18n.t("description_2"),
					field: "description2",
					width: 250,
				}),
				textColumn({
					headerName: i18n.t("category"),
					field: "categoryName",
					width: 200,
				}),
				textColumn({
					headerName: i18n.t("note"),
					field: "note",
					width: 200,
				}),
				textColumn({
					headerName: i18n.t("created_by"),
					field: "createdByUserName",
				}),
				dateTimeColumn({
					headerName: i18n.t("created_at"),
					field: "createdAt",
					width: 150,
				}),
				booleanColumn({
					headerName: i18n.t("pinned"),
					field: "isPinned",
				}),
				booleanColumn({
					headerName: i18n.t("object_main_document"),
					field: "isObjectMainDocument",
					width: 200,
				}),
			]}
			slots={{
				row: (rowProps) => (
					<GridRowWithFileDropping
						canDrop={editable}
						onFilesDropped={(files) => onFilesDroppedToRow(rowProps.row, files)}
						{...rowProps}
					/>
				),
			}}
			actionBarComponents={
				<>
					<LazyMultiSelectField
						label={i18n.t("category")}
						value={currentParams.categoryFilter}
						fetchOptions={() =>
							DocumentsOfObjectDataGridApi.getObjectDocumentCategoryOptions({
								objectRef,
							})
						}
						getOptionKey={(option) => option.documentCategoryId}
						getOptionLabel={(option) => option.name}
						onChange={async (values: number[]) => {
							await refreshData({ categoryFilter: values });
						}}
						sx={{
							minWidth: 120,
							display: { xs: "none", sm: "block" },
						}}
					/>
					<AavoTextField
						label={i18n.t("search")}
						onSubmit={async (value) => {
							await refreshData({ searchQuery: value });
						}}
					/>
					{editable && (
						<AsyncButton
							icon={faLink}
							label={i18n.t("link")}
							variant={"outlined"}
							onClick={async () => {
								openDialog(({ closeDialog }) => ({
									title: i18n.t("link_documents"),
									children: (
										<ConnectDocumentsToObjectView
											objectRef={objectRef}
											onDocumentsConnected={async () => {
												await closeDialog();
												await refreshData();
											}}
										/>
									),
								}));
							}}
						/>
					)}
					<AsyncButton
						icon={faFileArrowDown}
						label={i18n.t("download")}
						variant={"outlined"}
						disabled={selectedDocumentIds.length === 0}
						onClick={async () => {
							const fileHandle = await DocumentFileApi.downloadDocumentsAsSingleFile({
								documentIds: selectedDocumentIds,
							});
							downloadFile(fileHandle);
						}}
					/>
					<AavoButton
						label={i18n.t("open")}
						icon={faExternalLink}
						variant={"outlined"}
						disabled={!onlySelectedRow}
						sx={{
							display: { md: "none" },
						}}
						onClick={() => {
							if (onlySelectedRow === undefined) return;

							openDialog(() => ({
								title: onlySelectedRow.description1,
								children: (
									<DocumentPreviewView documentId={onlySelectedRow.documentId} />
								),
							}));
						}}
					/>
					{editable && <DocumentDropZone objectRef={objectRef} onSubmit={refreshData} />}
				</>
			}
			form={{
				dialogSize: "lg",
				dialogTitle: i18n.t("document_connection"),
				component: ({ row, onCompleted, onFormEdited }) => {
					return (
						<DocumentForm
							objectRef={objectRef}
							documentId={row?.documentId}
							onCompleted={onCompleted}
							onFormEdited={onFormEdited}
						/>
					);
				},
				addRowEnabled: editable,
				editEnabled: editable,
			}}
			remove={{
				type: editable ? "enabled" : "hidden",
				action: async ({ items }) => {
					await DocumentsOfObjectDataGridApi.deleteDocumentConnections({
						documentConnections: items,
					});
				},
			}}
			rowContextMenuComponents={(params) => (
				<DocumentsOfObjectDataGridContextMenu
					objectRef={objectRef}
					refresh={refreshData}
					editable={editable}
					{...params}
				/>
			)}
			{...serverSideDataGridProps}
		/>
	);
};
